// Visibilidade de fornecedores e categorias por etapa/versão do orçamento.
//
// `supplierList` e `addedItemTypeList` NÃO têm campo `version` — são as mesmas
// listas para Orçamento, Negociação e Fechamento. Quem sabe em quais etapas um
// item existe é o serviço, através de `service.versions`.
//
// Por isso a exclusão de fornecedor/categoria numa etapa versionada não remove
// nada dessas listas (isso apagaria o item do orçamento original também); ela só
// tira a versão atual de `service.versions`. Estas funções fazem o outro lado do
// contrato: escondem da tela o fornecedor/categoria que não tem nenhum serviço na
// versão sendo exibida.
//
// MMV: derivar a exclusão só dos serviços não cobre o item VAZIO (fornecedor
// "A DEFINIR" sem serviço nenhum, categoria recém-criada): não há `versions` de
// onde tirar a versão atual, e o atalho "sem serviço ⇒ visível" abaixo o mantinha
// na tela para sempre — o delete virava no-op silencioso, mesmo com o "Apagado"
// na tela e o autosave rodando. Para esses casos a exclusão é registrada
// explicitamente em `deletedInVersions` na própria entrada da lista (ver
// DELETE_SUPPLIER / DELETE_BUDGET_ITEM), que tem prioridade sobre o atalho.
//
// Forma de cobrança no fechamento/negociação pode divergir do orçamento
// (`closingValues.calcOption`). A visibilidade usa a forma da etapa, não só
// `service.calcOption`.

import { resolveServiceCalcOption } from "./budget-phase-calc-option";

type VersionedService = {
  supplierId?: string;
  type?: string;
  calcOption?: string;
  versions?: number[];
  budgetValues?: { calcOption?: string };
  negotiationValue?: Array<{ calcOption?: string } | undefined>;
  closingValues?: { calcOption?: string };
};

function stageOf(version: number, status?: string) {
  return { version, status };
}

function inVersion(service: VersionedService, version: number): boolean {
  return (service.versions ?? []).includes(version);
}

function existsInVersion(
  services: VersionedService[],
  version: number
): boolean {
  // Sem nenhum serviço: é um fornecedor/categoria recém-criado e ainda vazio.
  // Mantém visível, senão seria impossível montar um item novo dentro da
  // negociação/fechamento.
  if (services.length === 0) return true;
  return services.some((service) => inVersion(service, version));
}

/** A entrada da lista foi explicitamente excluída na versão exibida? */
function isDeletedInVersion(
  deletedInVersions: number[] | undefined,
  version: number
): boolean {
  return (deletedInVersions ?? []).includes(version);
}

function matchesIdentity(
  service: VersionedService,
  supplierId: string,
  type?: string
): boolean {
  return (
    service.supplierId === supplierId &&
    (type === undefined || service.type === type)
  );
}

/**
 * Serviços desta identidade na forma de cobrança da etapa. Se o serviço
 * existe na versão mas foi para outra forma (D→B no fechamento), a origem
 * some — não cai no atalho de "item vazio recém-criado".
 *
 * No CARD do fornecedor (sem `type`), `inThisVersion` só conta serviço da
 * mesma identidade de forma. Sem isso, um serviço vivo em B na mesma versão
 * escondia o card recém-criado em A do mesmo fornecedor — o preenchimento
 * "sumia" e o próximo "Novo fornecedor" virava o nº seguinte. Categoria
 * continua olhando qualquer forma: é o que esconde a origem de um move.
 */
function visibilityFromServices(
  budgetServices: VersionedService[] | undefined,
  supplierId: string,
  calcOption: string,
  version: number,
  type: string | undefined,
  deletedInVersions: number[] | undefined,
  status?: string
): boolean {
  if (isDeletedInVersion(deletedInVersions, version)) return false;

  const stage = stageOf(version, status);
  const inThisVersion = (budgetServices ?? []).filter((service) => {
    if (
      !matchesIdentity(service, supplierId, type) ||
      !inVersion(service, version)
    ) {
      return false;
    }
    // Card do fornecedor (sem categoria): serviço de OUTRO bloco não
    // ocupa este card. O mesmo fornecedor pode existir em A e em B; sem
    // este recorte o card novo herdava a ocupação do vizinho e sumia.
    // Categoria mantém o recorte antigo — é o que esconde a origem de um
    // move D→B que ainda não vale no orçamento (OPEN).
    if (type === undefined && (service.calcOption || "A") !== calcOption) {
      return false;
    }
    return true;
  });
  const owned = inThisVersion.filter(
    (service) => resolveServiceCalcOption(service, stage) === calcOption
  );

  if (owned.length > 0) return true;

  // Serviço cuja identidade é desta forma, mas que a etapa já moveu para
  // outra, não pode esconder o card se outros serviços — de identidade
  // diferente — resolvem para cá. Sem isto, no fechamento do MM 23692.26 o
  // card A da Berrini sumia: ROOMS/AEB/EQUIPMENTS orçados em B e movidos
  // para A ficavam invisíveis porque um Necessidades adicionais criado em A
  // e movido para B ainda ocupava `inThisVersion` pela identidade.
  const resolvedHere = (budgetServices ?? []).filter(
    (service) =>
      matchesIdentity(service, supplierId, type) &&
      resolveServiceCalcOption(service, stage) === calcOption
  );
  if (resolvedHere.length > 0) {
    return existsInVersion(resolvedHere, version);
  }

  if (inThisVersion.length > 0) return false;
  // Sem serviço nesta forma: item vazio recém-criado continua visível.
  return true;
}

/**
 * O fornecedor tem algum serviço na versão exibida (ou ainda não tem serviço algum)?
 *
 * `deletedInVersions` vem da própria entrada da `supplierList` e é o que
 * distingue "vazio porque é novo" (aparece) de "vazio porque foi apagado nesta
 * etapa" (some). Sem ele, fornecedor sem serviço é sempre visível.
 */
export function isSupplierVisibleInVersion(
  budgetServices: VersionedService[] | undefined,
  supplierId: string,
  calcOption: string,
  version: number,
  deletedInVersions?: number[],
  status?: string
): boolean {
  return visibilityFromServices(
    budgetServices,
    supplierId,
    calcOption,
    version,
    undefined,
    deletedInVersions,
    status
  );
}

/** A categoria tem algum serviço na versão exibida (ou ainda não tem serviço algum)? */
export function isCategoryVisibleInVersion(
  budgetServices: VersionedService[] | undefined,
  supplierId: string,
  type: string,
  calcOption: string,
  version: number,
  deletedInVersions?: number[],
  status?: string
): boolean {
  return visibilityFromServices(
    budgetServices,
    supplierId,
    calcOption,
    version,
    type,
    deletedInVersions,
    status
  );
}

/**
 * Existe ao menos um serviço alimentando esta identidade na etapa exibida?
 *
 * Diferente de `isSupplierVisibleInVersion`/`isCategoryVisibleInVersion`, que
 * respondem "deve aparecer na tela?" e por isso mantêm visível o item ainda
 * VAZIO (ver `existsInVersion`), esta pergunta é sobre conteúdo: item sem
 * serviço devolve `false`.
 *
 * É o desempate de `shouldUsePreservedTotals` — um total zerado só é
 * transitório enquanto houver serviço de onde ele possa vir.
 *
 * Passe `type` para restringir a uma categoria; omita para somar o fornecedor
 * inteiro naquela forma de cobrança.
 */
export function hasServicesInVersion(
  budgetServices: VersionedService[] | undefined,
  supplierId: string,
  calcOption: string,
  version: number,
  type?: string,
  status?: string
): boolean {
  const stage = stageOf(version, status);
  return (budgetServices ?? []).some(
    (service) =>
      service.supplierId === supplierId &&
      resolveServiceCalcOption(service, stage) === calcOption &&
      (type === undefined || service.type === type) &&
      (service.versions ?? []).includes(version)
  );
}

type SupplierListEntry = {
  supplierId?: string;
  calcOption?: string;
  deletedInVersions?: number[];
};

type AddedItemTypeEntry = {
  type?: string;
  supplierId?: string;
  calcOption?: string;
  deletedInVersions?: number[];
};

/**
 * Um serviço deve aparecer em sidebar / export / totais da etapa?
 *
 * Diferente de `isSupplierVisibleInVersion`: aquela pergunta é sobre o CARD
 * (fornecedor vazio recém-criado continua visível). Esta é sobre a LINHA de
 * serviço — sem entrada em `supplierList` para `supplierId`+`calcOption` o
 * serviço é órfão (invisível no editor) e não pode vazar para o restante.
 *
 * `supplierList` `undefined` (fixtures de totais sem a lista) não aplica o
 * filtro de pertinência; um array — inclusive vazio — aplica.
 *
 * `membershipCalcOption` troca a forma usada na pertinência. Por padrão é a
 * identidade (`service.calcOption`); quem já resolveu a forma da etapa (serviço
 * movido B→A no fechamento) passa a forma de destino, porque é o card de
 * destino que desenha o serviço no editor.
 */
export function isServiceVisibleInStage(
  service: VersionedService,
  supplierList: SupplierListEntry[] | undefined,
  version: number,
  membershipCalcOption?: string
): boolean {
  // Sem `versions`: legado, pertence a todas as etapas (igual ao filtro de
  // totais de negociação). Presente e sem a versão ⇒ escondido.
  if (service.versions && !service.versions.includes(version)) {
    return false;
  }

  if (!Array.isArray(supplierList)) {
    return true;
  }

  const calcOption = membershipCalcOption ?? service.calcOption;
  const row = supplierList.find(
    (entry) =>
      entry.supplierId === service.supplierId && entry.calcOption === calcOption
  );
  if (!row) return false;
  return !isDeletedInVersion(row.deletedInVersions, version);
}

export function filterServicesVisibleInStage<T extends VersionedService>(
  services: T[] | undefined,
  supplierList: SupplierListEntry[] | undefined,
  version: number
): T[] {
  return (services ?? []).filter((service) =>
    isServiceVisibleInStage(service, supplierList, version)
  );
}
function isCategoryDeletedInVersion(
  addedItemTypeList: AddedItemTypeEntry[] | undefined,
  supplierId: string | undefined,
  type: string | undefined,
  calcOption: string | undefined,
  version: number
): boolean {
  if (!supplierId || !type || !calcOption) return false;
  const entry = (addedItemTypeList ?? []).find(
    (el) =>
      el.supplierId === supplierId &&
      el.type === type &&
      el.calcOption === calcOption
  );
  return isDeletedInVersion(entry?.deletedInVersions, version);
}

/**
 * Célula da árvore de totais só entra na soma da etapa se a identidade
 * `(supplierId, type, calcOption)` tiver um serviço visível.
 *
 * Sem isso, leftover de categoria (RSVP em C sem serviço) e totais cacheados
 * num `|dup|` sem linha na etapa vazam no header, na sidebar e na barra.
 *
 * `budgetServices === undefined` (fixtures / list snapshot) mantém só a
 * pertinência em `supplierList` — o mesmo contrato do export.
 *
 * Não faz parse de `|dup|`: raw e `id|dup|1` são identidades distintas.
 *
 * MMV-2626: o serviço casa pela forma da ETAPA (`resolveServiceCalcOption`),
 * não pela identidade `service.calcOption`. Um Transfer movido B→A no
 * fechamento mantém `calcOption: "B"` e só `closingValues.calcOption: "A"`;
 * comparar pela identidade descartava a célula A (o valor real do
 * fechamento) como leftover e zerava a categoria na tela e no xlsx. A
 * pertinência também é na forma da etapa: sem card do fornecedor no destino
 * o serviço não aparece no editor e não pode entrar na soma. Sem `status` a
 * forma resolvida é a do orçamento — o comportamento anterior.
 */
export function isTotalsIdentityVisibleInStage(
  item: {
    supplierId?: string;
    calcOption?: string;
    type?: string;
    category?: string;
  },
  category: string,
  calcOption: string,
  version: number,
  supplierList: SupplierListEntry[] | undefined,
  budgetServices?: VersionedService[],
  addedItemTypeList?: AddedItemTypeEntry[],
  status?: string
): boolean {
  const supplierId = item?.supplierId;
  const itemCalcOption = item?.calcOption || calcOption;
  const type = item?.type || item?.category || category;

  if (
    isCategoryDeletedInVersion(
      addedItemTypeList,
      supplierId,
      type,
      itemCalcOption,
      version
    )
  ) {
    return false;
  }

  if (!Array.isArray(budgetServices)) {
    return isServiceVisibleInStage(
      { supplierId, calcOption: itemCalcOption },
      supplierList,
      version
    );
  }

  const stage = stageOf(version, status);
  return budgetServices.some(
    (service) =>
      service.supplierId === supplierId &&
      service.type === type &&
      resolveServiceCalcOption(service, stage) === itemCalcOption &&
      isServiceVisibleInStage(service, supplierList, version, itemCalcOption)
  );
}

/**
 * Serviço que a sidebar / totais da etapa podem listar.
 *
 * Depois do MMV-2607 o `service.calcOption` é a identidade do orçamento e a
 * forma da etapa vive em `closingValues.calcOption`. Um Aéreo movido B→A no
 * fechamento conta em A — onde o editor o desenha —, e a célula que sobrou
 * em B (cache da origem) sai da soma porque nenhum serviço resolve para B.
 * Esta função exige a mesma identidade visível que `isTotalsIdentityVisibleInStage`.
 *
 * MMV-2626-2: a identidade visível não basta — o PRÓPRIO serviço tem de estar
 * na etapa. Um Transfer excluído no fechamento (`versions: [0]`) ao lado de
 * outro Transfer vivo do mesmo fornecedor passava pela identidade e o painel
 * lateral somava os dois (4x Transfer em vez de 1x).
 */
export function isServiceCountedInStageTotals(
  service: VersionedService,
  version: number,
  status: string | undefined,
  supplierList: SupplierListEntry[] | undefined,
  budgetServices?: VersionedService[],
  addedItemTypeList?: AddedItemTypeEntry[]
): boolean {
  const calcOption = resolveServiceCalcOption(service, { version, status });
  if (!isServiceVisibleInStage(service, supplierList, version, calcOption)) {
    return false;
  }
  return isTotalsIdentityVisibleInStage(
    {
      supplierId: service.supplierId,
      calcOption,
      type: service.type,
    },
    service.type ?? "",
    calcOption,
    version,
    supplierList,
    budgetServices,
    addedItemTypeList,
    status
  );
}

export function filterServicesCountedInStageTotals<T extends VersionedService>(
  services: T[] | undefined,
  version: number,
  status: string | undefined,
  supplierList: SupplierListEntry[] | undefined,
  addedItemTypeList?: AddedItemTypeEntry[]
): T[] {
  const list = services ?? [];
  return list.filter((service) =>
    isServiceCountedInStageTotals(
      service,
      version,
      status,
      supplierList,
      list,
      addedItemTypeList
    )
  );
}
